import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getPieChartOptions } from "./chartConfig";

// Load Highcharts modules
require("highcharts/modules/exporting")(Highcharts);

const FindingsChart = ({
  loading,
  findingsCounts = {},
  isDatasetSelected = false,
  includeClean = true,
  showLegend = false,
}) => {
  const { totalFindings, criticalCount, warningCount } = findingsCounts;

  const [newOptions, setNewOptions] = useState({
    credits: { enabled: false },
    colors: ["#777"],
    title: { align: "center", text: "FINDINGS BY CRITICALITY" },
    chart: {
      height: 400,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: "#ccc",
    },
  });

  const chartComponent = useRef(null);

  const unavailableText = useMemo(() => {
    if (totalFindings && isDatasetSelected) return "";
    return !isDatasetSelected ? "No Dataset<br />Selected" : "No Records<br />Found";
  }, [isDatasetSelected, totalFindings]);

  const criticalityTypes = useMemo(() => {
    const criticalities = [
      {
        name: "Critical",
        y: criticalCount,
        color: "#EF5350",
      },
      {
        name: "Warning",
        y: warningCount,
        color: "#fbc02d",
      },
    ];

    return includeClean ? criticalities : criticalities.filter(({ y }) => y > 0);
  }, [criticalCount, includeClean, warningCount]);

  const updatedOptions = useMemo(
    () =>
      getPieChartOptions(Highcharts, {
        label: "FINDINGS BY CRITICALITY",
        unavailableText,
        showLegend,
        ...(totalFindings > 0 && isDatasetSelected
          ? {
              showLabels: true,
              dataSearies: [
                ...criticalityTypes,
                ...(includeClean
                  ? [
                      {
                        name: "Clean",
                        y: totalFindings - criticalCount - warningCount,
                        color: "#1B5E20",
                      },
                    ]
                  : []),
              ],
            }
          : { showLabels: false }),
      }),
    [
      criticalCount,
      criticalityTypes,
      includeClean,
      showLegend,
      isDatasetSelected,
      totalFindings,
      unavailableText,
      warningCount,
    ]
  );

  useEffect(() => {
    const chart = chartComponent.current?.chart;
    if (!chart || !Object.keys(chart).length) return;

    chart.showLoading("Loading...");

    if (loading) {
      chart.showLoading("Loading...");

      setNewOptions({
        credits: { enabled: false },
        chart: {
          height: 400,
          borderRadius: 15,
          borderWidth: 1,
          borderColor: "#ccc",
        },
      });
    } else {
      if (!isDatasetSelected) {
        chart.showLoading("No dataset selected");
        return;
      }

      chart.hideLoading();
      setNewOptions(updatedOptions);
    }
  }, [isDatasetSelected, loading, updatedOptions]);

  return <HighchartsReact key={includeClean} highcharts={Highcharts} ref={chartComponent} options={newOptions} />;
};

FindingsChart.propTypes = {
  loading: PropTypes.bool,
  findingsCounts: PropTypes.object,
  includeClean: PropTypes.bool,
  showLegend: PropTypes.bool,
  isDatasetSelected: PropTypes.bool,
};

export default FindingsChart;
